// Controller: wires the visual layer to anonymous auth, the public chat API, tap-to-talk
// recording, response audio/video sync, smart preload, replay, the policy gate and the
// inactivity reset.
import './styles.css';
import { ChatStore } from './store';
import { mountView, type ChatHandlers, type ViewApi } from './view';
import type { ChatConfig, ChatState, LanguageOption } from './types';
import { fetchConfig, interactAudio, interactText, interactVoiceText, type InteractResult } from './api';
import { initAuth } from './firebase';
import { mountLoadingLogo, type LoadingAnimation } from './loading';
import { Recorder } from './recorder';
import { SttStreamClient } from './stt-stream';
import { preloadLiveTranscript } from './live-transcript';
import { VideoPreloader, type VideoCategory } from './preloader';
import { setWaveformAmplitudeSource } from './waveform';
import {
  CANCEL_REDIRECT_URL,
  MAX_RECORDING_MS,
  MAX_RESPONSE_MS,
  MIN_SEND_CHARS,
  OUT_OF_TIME_MESSAGES,
  primarySubtag,
  STT_FINAL_TIMEOUT_MS,
  TRANSCRIPT_HOLD_MS,
} from './config';

// A 44-byte silent WAV: played within the policy-confirm gesture to unlock audio playback
// so the answer TTS isn't blocked by mobile autoplay policies.
const SILENT_WAV =
  'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=';

const piId = location.pathname.split('/').filter(Boolean)[0] ?? '';

const LOADING_LANG: LanguageOption = {
  code: 'en-US',
  label: 'EN',
  name: 'English',
  image: null,
  captions: { wait: [''], listening: [''], processing: [''], eager: [''] },
};
const LOADING_CONFIG: ChatConfig = {
  companyName: '',
  defaultLanguage: 'en-US',
  enabledLanguages: ['en-US'],
  policy: '',
  languages: [LOADING_LANG],
  files: [],
};

const store = new ChatStore({
  state: 'loading',
  config: LOADING_CONFIG,
  selectedLang: LOADING_LANG,
  inputValue: '',
  responseTranscript: '',
  liveTranscript: '',
  recPhase: 'recording',
  error: null,
  locked: true,
  overrideCaption: null,
});

const preloader = new VideoPreloader();

// --- Conversation state ---
let conversationId: string | null = null;
let history: { role: string; content: string }[] = [];
let lastResponse: { videoUrl: string | null; audioUrl: string | null } | null = null;

// --- Media handles ---
let mediaStream: MediaStream | null = null;
let recorder: Recorder | null = null;
let sttStream: SttStreamClient | null = null;
let responseAudio: HTMLAudioElement | null = null;
let transientTimer = 0;
let playbackTimer = 0;
let playbackGen = 0; // invalidates superseded playback chains

let view: ViewApi;

// =====================================================================
// State transitions (also drive the status video for the new state)
// =====================================================================

function categoryForState(state: ChatState): VideoCategory | null {
  switch (state) {
    case 'wait':
    case 'keyboard':
      return 'wait';
    case 'listening':
      return 'listening';
    case 'processing':
      return 'processing';
    case 'eager':
      return 'eager';
    default:
      return null; // response (supporting video), policy (overlay)
  }
}

function goState(newState: ChatState): void {
  if (store.state.locked) return; // locked is the single source of truth for blocking
  clearPlaybackTimer();
  playbackGen++; // any in-flight response playback is now stale
  store.set({ state: newState, overrideCaption: null, liveTranscript: '', recPhase: 'recording' });
  const cat = categoryForState(newState);
  // In the background (e.g. a cancelled recording settling late) onVisible() starts it.
  if (cat && document.visibilityState !== 'hidden') playStateVideo(cat);
  // The conversation persists in the `eager` state (no inactivity reset): the history is
  // kept and re-sent in full on every follow-up until the page is reloaded.
}

let queuedGen = 0; // bumped whenever the queued (back) video is dropped

/** Drop the video buffered in the back element; any pending hand-over becomes stale. */
function cancelQueuedVideo(): void {
  queuedGen++;
  const back = view.backVideo;
  back.oncanplay = null;
  back.onerror = null;
  back.pause();
  if (back.hasAttribute('src')) {
    back.removeAttribute('src');
    back.load();
  }
}

/**
 * Buffer the category's next video in the hidden back element and hand over to it when
 * the visible one ends. The visible video keeps showing its last frame until the next one
 * is actually playing, so there is no black frame between two status videos.
 */
function queueNextStateVideo(cat: VideoCategory): void {
  const myGen = ++queuedGen;
  const front = view.video;
  const back = view.backVideo;
  const url = preloader.pick(cat);
  preloader.preload(cat);
  if (!url) return;
  back.loop = false;
  back.muted = true;
  back.src = url;
  back.load();

  front.onended = () => {
    if (myGen !== queuedGen) return;
    const handOver = () => {
      back.oncanplay = null;
      back.onerror = null;
      back
        .play()
        .then(() => {
          if (myGen !== queuedGen) return;
          front.onended = null;
          view.swapVideos();
          front.pause();
          queueNextStateVideo(cat);
        })
        .catch(() => {});
    };
    // The next video failed to load: replay the current one and try the one after it.
    const replay = () => {
      if (myGen !== queuedGen) return;
      front.currentTime = 0;
      front.play().catch(() => {});
      queueNextStateVideo(cat);
    };
    if (back.error) replay();
    else if (back.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) handOver();
    else {
      back.oncanplay = () => {
        if (myGen === queuedGen) handOver();
      };
      back.onerror = replay;
    }
  };
}

function playStateVideo(cat: VideoCategory): void {
  stopAudio();
  cancelQueuedVideo();
  const video = view.video;
  video.onended = null;
  const url = preloader.pick(cat);
  preloader.preload(cat); // warm ~2 for this category, evict others
  if (url) {
    // With several videos in the category, play them one after the other instead of
    // looping the first pick for as long as the state lasts.
    const rotate = preloader.count(cat) > 1;
    video.loop = !rotate;
    video.muted = true;
    if (video.getAttribute('src') !== url) video.src = url;
    video.load();
    view.screen.classList.add('video-playing');
    video.play().catch(() => view.screen.classList.remove('video-playing'));
    if (rotate) queueNextStateVideo(cat);
  } else {
    video.removeAttribute('src');
    video.load();
    view.screen.classList.remove('video-playing');
  }
}

// =====================================================================
// Recording (tap to start, pause/append, then send or discard; MAX_RECORDING_MS budget)
// =====================================================================

async function ensureMic(): Promise<boolean> {
  // A cached mic stream goes stale on iOS Safari: playing the TTS answer switches the
  // audio session to playback, muting/ending the getUserMedia track — a reused stream then
  // records silence and the turn is discarded. Only reuse a verifiably live, unmuted track;
  // otherwise re-acquire (no re-prompt: permission persists for the page session).
  const track = mediaStream?.getAudioTracks()[0];
  if (track && track.readyState === 'live' && !track.muted) return true;
  releaseMic();
  try {
    mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });
    return true;
  } catch {
    showTransient('Microphone access is needed to talk. You can also type your question.');
    return false;
  }
}

function releaseMic(): void {
  mediaStream?.getTracks().forEach((t) => t.stop());
  mediaStream = null;
  recorder = null;
}

async function startRecording(): Promise<void> {
  if (!(await ensureMic()) || !mediaStream) return;
  recorder = new Recorder(mediaStream);
  goState('listening');
  preloadLiveTranscript(); // warm anime.js before the first word arrives

  // Live STT stream for the word-by-word caption (mirrors the kiosk). Everything is
  // best-effort: if the socket, the PCM tap, or the final transcript fails, the turn
  // falls back to the classic whole-blob audio upload below.
  sttStream?.abort(); // any stray previous session is dead by now
  const stream = new SttStreamClient();
  sttStream = stream;
  stream.onTranscript = (text) => {
    if (sttStream === stream && store.state.state === 'listening') {
      store.set({ liveTranscript: text });
    }
  };
  void stream.start(piId, store.state.selectedLang.code);

  setWaveformAmplitudeSource(() => recorder?.amplitude() ?? 0);
  recorder.start(
    (blob) => {
      setWaveformAmplitudeSource(null);
      // Release the mic after every recording: the stream cannot survive the answer's TTS
      // playback on iOS Safari anyway (see ensureMic), and dropping it also clears the
      // browser's recording indicator between turns.
      releaseMic();
      // too short / empty / backgrounded — stay in the conversation if one is in progress
      if (blob && document.visibilityState !== 'hidden') {
        store.set({ recPhase: 'sending' }); // also when the recorder stopped by itself
        void finishVoiceTurn(blob, stream);
      } else {
        stream.abort();
        if (sttStream === stream) sttStream = null;
        goState(conversationId ? 'eager' : 'wait');
      }
    },
    (chunk) => stream.feed(chunk),
    () => {
      // Out of time: the recorder has paused itself. Nothing is sent until the visitor
      // taps send (or discards the recording).
      if (store.state.state !== 'listening') return;
      store.set({ recPhase: 'out-of-time' });
      showOutOfTime();
    },
  );
}

/** Stop and send. The controls stay disabled while the final transcript is awaited. */
function stopRecording(): void {
  if (!recorder || store.state.recPhase === 'sending') return;
  store.set({ recPhase: 'sending' });
  recorder.stop();
}

/** Record button while `listening`: pause, or append to the paused recording. */
function toggleRecordingPause(): void {
  if (!recorder) return;
  switch (store.state.recPhase) {
    case 'recording':
      recorder.pause();
      store.set({ recPhase: 'paused' });
      break;
    case 'paused':
      if (recorder.resume()) store.set({ recPhase: 'recording' });
      break;
    case 'out-of-time':
      showOutOfTime();
      break;
  }
}

/** Trash button: throw the recording away and return to the idle screen. */
function discardRecording(): void {
  if (!recorder || store.state.recPhase === 'sending') return;
  recorder.cancel(); // onStop receives null → aborts the stream and leaves `listening`
}

function showOutOfTime(): void {
  const code = primarySubtag(store.state.selectedLang.code);
  showTransient(OUT_OF_TIME_MESSAGES[code] ?? OUT_OF_TIME_MESSAGES.en);
}

/**
 * Recording finished: prefer the live-stream transcript (transcribed exactly once,
 * like the kiosk) — finish the stream, wait briefly for the final text while the
 * sentence stays on screen, then send it as a voice_text interaction. Any failure
 * (stream never connected, no PCM fed, timeout, empty/silent transcript) falls back
 * to the classic whole-blob audio upload, so nothing is ever lost.
 */
async function finishVoiceTurn(blob: Blob, stream: SttStreamClient): Promise<void> {
  if (stream.usable) {
    stream.finish();
    const finalText = ((await stream.waitForFinal(STT_FINAL_TIMEOUT_MS)) || '').trim();
    // Superseded while waiting (reset, new recording, dev state switch) — drop the turn.
    if (sttStream !== stream || store.state.state !== 'listening') {
      stream.abort();
      return;
    }
    sttStream = null;
    if (finalText) {
      store.set({ liveTranscript: finalText });
      await sendVoiceText(finalText);
      return;
    }
  }
  stream.abort();
  if (sttStream === stream) sttStream = null;
  if (store.state.state !== 'listening') return;
  sendAudio(blob);
}

/**
 * Send the streamed transcript as a voice_text interaction. The request starts
 * immediately, but the finished sentence stays on screen (still in `listening`)
 * for TRANSCRIPT_HOLD_MS so the visitor can read what was understood — the hold
 * overlaps the network round-trip, exactly like the kiosk's transcript hold.
 */
async function sendVoiceText(text: string): Promise<void> {
  lastResponse = null;
  store.set({ responseTranscript: '' });
  const request = interactVoiceText(
    piId, text, store.state.selectedLang.code, conversationId, historyJson(),
  ).then(
    (result) => ({ result }),
    () => ({ result: null }),
  );
  await new Promise((r) => setTimeout(r, TRANSCRIPT_HOLD_MS));
  if (store.state.state === 'listening') goState('processing');
  const { result } = await request;
  if (result) handleResult(result);
  else afterFailedRequest();
}

// =====================================================================
// Send to backend
// =====================================================================

function historyJson(): string {
  // Send the entire conversation history — it is never truncated or reset.
  return JSON.stringify(history);
}

function beginTurn(): void {
  lastResponse = null;
  store.set({ responseTranscript: '' });
  goState('processing');
}

async function sendAudio(blob: Blob): Promise<void> {
  beginTurn();
  try {
    handleResult(
      await interactAudio(piId, blob, store.state.selectedLang.code, conversationId, historyJson()),
    );
  } catch {
    afterFailedRequest();
  }
}

async function sendText(text: string): Promise<void> {
  store.set({ inputValue: '' });
  beginTurn();
  try {
    handleResult(
      await interactText(piId, text, store.state.selectedLang.code, conversationId, historyJson()),
    );
  } catch {
    afterFailedRequest();
  }
}

function afterFailedRequest(): void {
  goState(conversationId ? 'eager' : 'wait');
  showTransient('Something went wrong. Please try again.');
}

// =====================================================================
// Handle the interact result
// =====================================================================

function handleResult(result: InteractResult): void {
  const { status, data } = result;

  if (status === 429) {
    if (data.error === 'daily_limit_reached') {
      clearPlaybackTimer();
      playbackGen++;
      releaseMic();
      store.set({
        state: 'wait',
        locked: true,
        overrideCaption: data.error_message || "You've reached the daily question limit.",
      });
      return;
    }
    goState(conversationId ? 'eager' : 'wait');
    showTransient(data.error_message || 'You are sending messages too quickly. Please wait a moment.');
    return;
  }

  if (status === 200 && data.answer) {
    playResponse(result);
    return;
  }

  // Non-200 (402 out-of-credits, 5xx, validation, or empty 200): show the message
  // transiently — do NOT play it or record it as a conversation turn.
  goState(conversationId ? 'eager' : 'wait');
  showTransient(
    data.error_message || data.answer || 'Sorry, something went wrong. Please try again.',
  );
}

function resolveSupportingUrl(filename: string | null): string | null {
  if (!filename) return null;
  const f = store.state.config.files.find((x) => x.filename === filename);
  return f ? f.mobileUrl || f.url : null;
}

function playResponse(result: InteractResult): void {
  const data = result.data;
  conversationId = data.conversation_id || conversationId;
  if (data.transcribed_text) history.push({ role: 'user', content: data.transcribed_text });
  if (data.answer) history.push({ role: 'assistant', content: data.answer });

  const videoUrl = resolveSupportingUrl(data.supporting_file);
  const audioUrl = data.answer_audio_url;
  lastResponse = { videoUrl, audioUrl };

  // Enter response without goState so the answer caption is not clobbered.
  store.set({ state: 'response', responseTranscript: data.answer, overrideCaption: null });
  // Answer arrived while the page is in the background: onVisible() plays it on return.
  if (document.visibilityState === 'hidden') return;
  playMedia(videoUrl, audioUrl, () => goState('eager'));
}

// =====================================================================
// Synchronized response playback (mirrors test-chat responding logic),
// hardened with a generation token + one-shot completion + watchdog.
// =====================================================================

function clearPlaybackTimer(): void {
  if (playbackTimer) {
    clearTimeout(playbackTimer);
    playbackTimer = 0;
  }
}

function ensureAudio(): HTMLAudioElement {
  if (!responseAudio) responseAudio = new Audio();
  return responseAudio;
}

function stopAudio(): void {
  if (responseAudio) {
    responseAudio.pause();
    responseAudio.onended = null;
    responseAudio.onerror = null;
    responseAudio.removeAttribute('src');
  }
}

/** Best-effort unlock of audio autoplay; call from a user gesture (policy confirm). */
function unlockAudio(): void {
  try {
    const a = ensureAudio();
    a.src = SILENT_WAV;
    const p = a.play();
    if (p) p.then(() => a.pause()).catch(() => {});
  } catch {
    /* ignore */
  }
}

function playMedia(videoUrl: string | null, audioUrl: string | null, onComplete: () => void): void {
  const myGen = ++playbackGen;
  clearPlaybackTimer();
  stopAudio();
  cancelQueuedVideo();
  const video = view.video;
  video.onended = null;

  let completed = false;
  const finish = () => {
    if (completed || myGen !== playbackGen) return;
    completed = true;
    clearPlaybackTimer();
    onComplete();
  };

  if (!videoUrl && !audioUrl) {
    // Text-only answer: show the caption for 5s, then continue.
    view.screen.classList.remove('video-playing');
    playbackTimer = window.setTimeout(finish, 5000);
    return;
  }

  // Audio is the master track (the user must hear the full answer). Coordination rule:
  //  - audio LONGER  : video loops to keep visuals going; the AUDIO's end cuts it off
  //                    immediately, even mid-loop. Total = audio duration.
  //  - video LONGER  : video plays once to its natural end (no cut); audio finishes
  //                    earlier underneath. Total = video duration.
  // Playback ends when audio is done AND the video has stopped (looped→cut, or, when video
  // is the longer track, finished its single natural play).
  let audioFinished = false;
  let videoFinished = false;
  let videoPlayedOnce = false; // true once the video has completed at least one full pass
  const done = () => {
    if (myGen === playbackGen && audioFinished && videoFinished) finish();
  };

  // Stop and hide the video. Used to cut the looping (shorter) video at the audio's end.
  const cutVideo = () => {
    if (videoFinished) return;
    video.onended = null;
    video.pause();
    view.screen.classList.remove('video-playing');
    videoFinished = true;
  };

  // Watchdog: a stalled element may never fire ended/error — force completion eventually.
  playbackTimer = window.setTimeout(() => {
    audioFinished = true;
    videoFinished = true;
    done();
  }, MAX_RESPONSE_MS);

  if (videoUrl) {
    view.screen.classList.add('video-playing');
    video.loop = false; // looping handled manually so we can stop on the audio's end
    video.muted = true; // the answer audio is the TTS track; keep the supporting video silent
    video.src = videoUrl;
    video.onended = () => {
      if (myGen !== playbackGen) return;
      videoPlayedOnce = true;
      if (audioFinished) {
        // Audio already done → video is the longer track; its natural end ends playback.
        videoFinished = true;
        done();
      } else {
        // Audio still playing → keep visuals going by looping until the audio ends.
        video.currentTime = 0;
        video.play().catch(() => {});
      }
    };
    video.load();
    video.play().catch(() => {
      videoFinished = true;
      done();
    });
  } else {
    videoFinished = true;
    view.screen.classList.remove('video-playing');
  }

  if (audioUrl) {
    const a = ensureAudio();
    a.pause();
    a.onended = () => {
      audioFinished = true;
      // If the video has already played through at least once, it's looping as filler under a
      // longer audio — cut it now at the audio's end. If it hasn't finished its first pass yet,
      // the video is the longer track: let it play out naturally (videoFinished set by its onended).
      if (videoPlayedOnce) cutVideo();
      done();
    };
    a.onerror = () => {
      audioFinished = true;
      if (videoPlayedOnce) cutVideo();
      done();
    };
    a.src = audioUrl;
    a.play().catch(() => {
      // Autoplay blocked: degrade gracefully — the answer text remains as the caption.
      // Let the (silent) video play to its natural end rather than cutting it short.
      audioFinished = true;
      if (!videoUrl) videoFinished = true;
      done();
    });
  } else {
    audioFinished = true;
  }
}

// =====================================================================
// Transient messages
// =====================================================================

function showTransient(message: string): void {
  if (transientTimer) clearTimeout(transientTimer);
  store.set({ overrideCaption: message });
  transientTimer = window.setTimeout(() => {
    if (store.state.overrideCaption === message) store.set({ overrideCaption: null });
  }, 3500);
}

// =====================================================================
// Handlers
// =====================================================================

const handlers: ChatHandlers = {
  onPolicyConfirm: () => {
    unlockAudio();
    goState('wait');
  },
  onPolicyCancel: () => {
    window.location.href = CANCEL_REDIRECT_URL;
  },
  onMicTap: () => {
    if (store.state.locked) return;
    const s = store.state.state;
    if (s === 'listening') toggleRecordingPause();
    else if (s === 'wait' || s === 'eager' || s === 'keyboard') void startRecording();
  },
  onTrashTap: () => {
    if (store.state.locked) return;
    if (store.state.state === 'listening') discardRecording();
  },
  onSendTap: () => {
    if (store.state.locked) return;
    const s = store.state.state;
    if (s === 'listening') {
      stopRecording();
    } else if (
      (s === 'wait' || s === 'eager' || s === 'keyboard') &&
      store.state.inputValue.trim().length >= MIN_SEND_CHARS
    ) {
      void sendText(store.state.inputValue.trim());
    }
  },
  onLangSelect: (lang: LanguageOption) => {
    if (store.state.locked) return;
    const s = store.state.state;
    if (s === 'listening' || s === 'processing' || s === 'response') return;
    store.set({ selectedLang: lang });
  },
  onReplayTap: () => {
    if (store.state.locked) return;
    if (store.state.state === 'response' && lastResponse) {
      playMedia(lastResponse.videoUrl, lastResponse.audioUrl, () => goState('eager'));
    }
  },
  onInputFocus: () => {
    if (store.state.locked) return;
    const s = store.state.state;
    if (s === 'wait' || s === 'eager') store.set({ state: 'keyboard' });
  },
  onInputBlur: () => {
    if (store.state.state === 'keyboard') store.set({ state: 'wait' });
  },
  onInputChange: (value: string) => store.set({ inputValue: value }),
  recordingRemainingMs: () => recorder?.remainingMs() ?? MAX_RECORDING_MS,
};

// Free the mic, playback and video buffers when the page is backgrounded or closed.
function onHidden(): void {
  if (!view) return;
  recorder?.cancel(); // a running or paused recording is discarded, not sent
  releaseMic();
  sttStream?.abort();
  sttStream = null;
  // The answer playback is torn down here; onVisible() restarts it from the beginning.
  clearPlaybackTimer();
  playbackGen++;
  stopAudio();
  // Last, so a state change triggered by the cancelled recording can't restart a video.
  cancelQueuedVideo();
  view.video.onended = null;
  view.video.pause();
  preloader.clear();
}

/**
 * Back in the foreground: nothing paused in onHidden resumes by itself (a paused status
 * video never fires `ended`, so the rotation would stay frozen), so restart the current
 * state's media from scratch.
 */
function onVisible(): void {
  if (!view || store.state.locked) return;
  const s = store.state.state;
  if (s === 'response') {
    if (lastResponse) playMedia(lastResponse.videoUrl, lastResponse.audioUrl, () => goState('eager'));
    else goState('eager');
    return;
  }
  const cat = categoryForState(s);
  if (cat) playStateVideo(cat);
}

document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') onHidden();
  else onVisible();
});
window.addEventListener('pagehide', onHidden);
// Restored from the back/forward cache: pagehide already tore the media down.
window.addEventListener('pageshow', (e) => {
  if (e.persisted) onVisible();
});

// =====================================================================
// Boot
// =====================================================================

// Minimum time the branded loading screen stays up, so the animation is visible even when the
// real work finishes almost instantly. Boot proceeds to policy only once work is done AND this
// has elapsed.
const MIN_LOADING_MS = 1200;

let loadingAnim: LoadingAnimation | null = null;

/**
 * Single source of truth for the loading-screen animation lifecycle: start the morph when we
 * enter the `loading` state, tear it down when we leave. Driven reactively from the store, so it
 * works identically whether `loading` is entered by boot() on cold start OR by the dev state
 * switcher (devbar). The morph itself is best-effort (reduced-motion / load failure → static logo).
 */
function syncLoadingAnimation(state: ChatState): void {
  if (state === 'loading') {
    if (!loadingAnim) {
      view.setLoadingProgress(0.06); // fresh bar (boot overrides this as its steps complete)
      loadingAnim = mountLoadingLogo(view.loadingLogo);
    }
  } else if (loadingAnim) {
    loadingAnim.destroy();
    loadingAnim = null;
  }
}

/** Show a blocking error and leave the loading screen (so the error overlay is visible). */
function bootError(message: string): void {
  // 'wait' (locked) is the neutral backdrop for the blocking error overlay; the loading
  // overlay (data-state='loading') would otherwise sit on top of it. Leaving 'loading' here
  // also tears down the morph via syncLoadingAnimation.
  store.set({ state: 'wait', error: message, locked: true });
}

async function boot(): Promise<void> {
  const startedAt = Date.now();
  view.setLoadingProgress(0.04);

  if (!piId) {
    bootError('No device specified. Open this page as /<device-id>.');
    return;
  }

  // --- Heavy lifting (real boot work drives the pill) ---
  // 1) Firebase init + anonymous sign-in + App Check token PRE-WARM. initAuth() already primes
  //    the first App Check token (bounded by APPCHECK_PRIME_TIMEOUT_MS) and awaits anon sign-in,
  //    so by the time this resolves the first real chat request after consent already has a
  //    cached token — proactively closing the first-request race. We do NOT weaken that fix.
  try {
    await initAuth();
  } catch {
    bootError('Could not connect. Please check your connection and try again.');
    return;
  }
  view.setLoadingProgress(0.5);

  // 2) Load the Pi config.
  const res = await fetchConfig(piId);
  if (res.error || !res.config) {
    bootError(
      res.error === 'chat_not_enabled'
        ? 'This assistant is not available.'
        : 'Could not load this assistant. Please try again later.',
    );
    return;
  }
  const config = res.config;
  if (!config.languages.length) {
    bootError('This assistant has no supported languages.');
    return;
  }
  view.setLoadingProgress(0.78);

  // 3) Warm the first-needed media so the experience is snappy right after consent. The 'wait'
  //    video is what plays the moment the user accepts the policy.
  preloader.setFiles(config.files);
  preloader.preload('wait');
  view.setLoadingProgress(0.95);

  const selectedLang =
    config.languages.find((l) => l.code === config.defaultLanguage) ?? config.languages[0];

  // 4) Hold for the minimum display time so the branded animation is actually seen.
  const remaining = MIN_LOADING_MS - (Date.now() - startedAt);
  if (remaining > 0) await new Promise((r) => setTimeout(r, remaining));
  view.setLoadingProgress(1);

  // Transition to policy; syncLoadingAnimation tears down the morph on the state change.
  store.set({
    config,
    selectedLang,
    error: null,
    locked: false,
    overrideCaption: null,
    state: 'policy',
  });
}

const appRoot = document.getElementById('app');
if (appRoot) {
  view = mountView(appRoot, store, handlers);
  // Drive the loading-screen morph from state, so it starts/stops correctly for both cold-start
  // boot and the dev state switcher. Fires immediately with the current ('loading') state.
  store.subscribe((s) => syncLoadingAnimation(s.state));
  void boot();
}
